<template>
  <div class="integration">
    <header class="hero">
      <div>
        <p class="eyebrow">Setup</p>
        <h1>Integration</h1>
        <p class="hero-sub">
          Connect ServiceM8 to {{ brand.agencyName }}, then choose how jobs and contacts should match.
        </p>
      </div>
      <div class="hero-side">
        <span class="status-chip" :class="connectionTone">
          <span class="status-dot" />
          {{ connectionLabel }}
        </span>
      </div>
    </header>

    <div v-if="isOverview" class="overview">
      <div class="search-wrap">
        <Search class="search-icon" :size="16" />
        <input
          v-model.trim="search"
          type="search"
          class="search-input"
          placeholder="Search settings…"
        />
      </div>

      <p v-if="!filteredGroups.length" class="empty">
        Nothing matches “{{ search }}”. Try a different word, such as jobs, contacts, or badges.
      </p>

      <section
        v-for="group in filteredGroups"
        :key="group.id"
        class="card group-card"
      >
        <div class="group-head">
          <h2>{{ group.title }}</h2>
          <p>{{ group.description }}</p>
        </div>
        <div class="tile-grid">
          <button
            v-for="item in group.items"
            :key="item.key"
            type="button"
            class="setting-tile"
            @click="openSubTab(item.key)"
          >
            <span class="tile-icon">
              <component :is="item.icon" :size="20" />
            </span>
            <span class="tile-copy">
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </span>
            <ChevronRight class="tile-chevron" :size="18" />
          </button>
        </div>
      </section>
    </div>

    <div v-else class="section-view">
      <div class="section-toolbar">
        <button type="button" class="back-btn" @click="openSubTab('Overview')">
          <ArrowLeft :size="16" />
          All settings
        </button>
        <div class="section-titles">
          <h2>{{ currentItem.title }}</h2>
          <p>{{ currentItem.description }}</p>
        </div>
        <label class="jump-label">
          Jump to
          <select :value="normalizedActiveKey" @change="openSubTab($event.target.value)">
            <option
              v-for="item in allItems"
              :key="item.key"
              :value="item.key"
            >
              {{ item.title }}
            </option>
          </select>
        </label>
      </div>

      <div class="section-body">
        <JobMappingTab v-if="activeSubTab === 'Job Mapping'" />
        <JobSyncMappingTab v-if="activeSubTab === 'Job Field Mapping'" />
        <CustomerSyncMappingTab v-if="activeSubTab === 'Client Mapping'" />
        <BadgeMappingTab v-if="activeSubTab === 'Badge Mapping'" />
        <QueueMappingTab v-if="activeSubTab === 'Queue Mapping'" />
        <StatusMappingTab v-if="activeSubTab === 'Status Mapping'" />
        <ConnectionsTab v-if="activeSubTab === 'Connections'" />
        <CustomFieldsTab v-if="activeSubTab === 'Custom Fields'" />
        <ManualSyncTab v-if="isSyncTab" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  ArrowLeft,
  Briefcase,
  ChevronRight,
  Columns3,
  Contact,
  ListOrdered,
  Plug,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Tag,
  ToggleRight,
} from 'lucide-vue-next';
import { brandMixin } from '../brandMixin';
import JobMappingTab from './JobMappingTab.vue';
import JobSyncMappingTab from './JobSyncMappingTab.vue';
import CustomerSyncMappingTab from './CustomerSyncMappingTab.vue';
import BadgeMappingTab from './BadgeMappingTab.vue';
import QueueMappingTab from './QueueMappingTab.vue';
import StatusMappingTab from './StatusMappingTab.vue';
import ConnectionsTab from './ConnectionsTab.vue';
import CustomFieldsTab from './CustomFieldsTab.vue';
import ManualSyncTab from './ManualSyncTab.vue';

const BASE_TABS = [
  'Job Mapping',
  'Job Field Mapping',
  'Client Mapping',
  'Badge Mapping',
  'Queue Mapping',
  'Status Mapping',
  'Connections',
  'Custom Fields',
  'Manual Sync',
];

export default {
  name: 'IntegrationTab',
  mixins: [brandMixin],
  components: {
    ArrowLeft,
    Briefcase,
    ChevronRight,
    Columns3,
    Contact,
    ListOrdered,
    Plug,
    RefreshCw,
    Search,
    SlidersHorizontal,
    Tag,
    ToggleRight,
    JobMappingTab,
    JobSyncMappingTab,
    CustomerSyncMappingTab,
    BadgeMappingTab,
    QueueMappingTab,
    StatusMappingTab,
    ConnectionsTab,
    CustomFieldsTab,
    ManualSyncTab,
  },
  props: {
    initialSubTab: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      subTabs: [...BASE_TABS],
      activeSubTab: 'Overview',
      initialSyncAt: undefined,
      search: '',
      connection: {
        connected: false,
        accountValid: true,
        loaded: false,
      },
    };
  },
  computed: {
    isOverview() {
      return !this.activeSubTab || this.activeSubTab === 'Overview';
    },
    isSyncTab() {
      return this.activeSubTab === 'Manual Sync' || this.activeSubTab === 'Initial Sync';
    },
    needsInitialSync() {
      return this.initialSyncAt === null;
    },
    syncTabKey() {
      return this.needsInitialSync ? 'Initial Sync' : 'Manual Sync';
    },
    syncTitle() {
      return this.needsInitialSync ? 'First sync' : 'Sync contacts and jobs';
    },
    syncDescription() {
      return this.needsInitialSync
        ? `Copy existing ServiceM8 clients and jobs into ${this.brand.agencyName} for the first time.`
        : `Copy contacts and jobs between ServiceM8 and ${this.brand.agencyName} whenever you need.`;
    },
    groups() {
      return [
        {
          id: 'setup',
          title: 'Get started',
          description: `Connect accounts and copy records between ServiceM8 and ${this.brand.agencyName}.`,
          items: [
            {
              key: 'Connections',
              title: 'Connect accounts',
              description: 'Link ServiceM8 and confirm this location is signed in.',
              icon: 'Plug',
            },
            {
              key: this.syncTabKey,
              title: this.syncTitle,
              description: this.syncDescription,
              icon: 'RefreshCw',
            },
          ],
        },
        {
          id: 'fields',
          title: 'Match fields',
          description: `Choose which details copy between a ServiceM8 job or client and ${this.brand.agencyName}.`,
          items: [
            {
              key: 'Job Mapping',
              title: 'Create jobs from workflows',
              description: `When a workflow runs, create a ServiceM8 job and fill in the details from ${this.brand.agencyName}.`,
              icon: 'Briefcase',
            },
            {
              key: 'Job Field Mapping',
              title: 'Match job fields',
              description: 'Keep linked jobs and opportunities updated both ways.',
              icon: 'Columns3',
            },
            {
              key: 'Client Mapping',
              title: 'Match contact fields',
              description: `Map ServiceM8 client details to contact fields in ${this.brand.agencyName}.`,
              icon: 'Contact',
            },
          ],
        },
        {
          id: 'progress',
          title: 'Job progress',
          description: 'Keep badges, queues, and job status in step with tags and pipeline stages.',
          items: [
            {
              key: 'Badge Mapping',
              title: 'Badges and tags',
              description: `When a ServiceM8 badge is added, apply the matching tag in ${this.brand.agencyName}.`,
              icon: 'Tag',
            },
            {
              key: 'Queue Mapping',
              title: 'Queues and stages',
              description: `Send a job in a ServiceM8 queue to the matching pipeline stage.`,
              icon: 'ListOrdered',
            },
            {
              key: 'Status Mapping',
              title: 'Job status',
              description: 'Move the linked opportunity when a ServiceM8 job status changes.',
              icon: 'ToggleRight',
            },
          ],
        },
        {
          id: 'advanced',
          title: 'Advanced',
          description: 'Optional ServiceM8 extras you only need if you use custom fields.',
          items: [
            {
              key: 'Custom Fields',
              title: 'ServiceM8 custom fields',
              description: 'Create extra fields on ServiceM8 jobs or clients.',
              icon: 'SlidersHorizontal',
            },
          ],
        },
      ];
    },
    allItems() {
      return this.groups.flatMap((group) => group.items);
    },
    filteredGroups() {
      const query = this.search.toLowerCase();
      if (!query) return this.groups;
      return this.groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            const haystack = `${item.title} ${item.description} ${item.key}`.toLowerCase();
            return haystack.includes(query);
          }),
        }))
        .filter((group) => group.items.length);
    },
    normalizedActiveKey() {
      if (this.isSyncTab) return this.syncTabKey;
      return this.activeSubTab;
    },
    currentItem() {
      return (
        this.allItems.find((item) => item.key === this.normalizedActiveKey) || {
          key: this.activeSubTab,
          title: this.activeSubTab,
          description: '',
        }
      );
    },
    connectionTone() {
      if (!this.connection.loaded) return 'tone-idle';
      if (this.connection.connected && this.connection.accountValid) return 'tone-ok';
      if (this.connection.connected) return 'tone-warn';
      return 'tone-bad';
    },
    connectionLabel() {
      if (!this.connection.loaded) return 'Checking ServiceM8…';
      if (this.connection.connected && this.connection.accountValid) return 'ServiceM8 connected';
      if (this.connection.connected) return 'ServiceM8 needs reconnecting';
      return 'ServiceM8 not connected';
    },
  },
  watch: {
    initialSubTab(value) {
      if (value) this.openSubTab(value);
    },
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const subTab = this.initialSubTab || urlParams.get('subTab');
    if (subTab) {
      this.openSubTab(subTab);
      if (urlParams.get('subTab')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    await Promise.all([this.checkInitialSyncStatus(), this.checkConnectionStatus()]);
  },
  methods: {
    openSubTab(name) {
      if (!name || name === 'Overview') {
        this.activeSubTab = 'Overview';
        return;
      }
      if (name === 'Manual Sync' || name === 'Initial Sync') {
        this.activeSubTab = this.syncTabKey;
        this.syncSubTabsAlias();
        return;
      }
      if (BASE_TABS.includes(name) || name === 'Initial Sync') {
        this.activeSubTab = name;
      }
    },
    handleSubTabChange(subTab) {
      this.openSubTab(subTab);
    },
    syncSubTabsAlias() {
      const next = [...BASE_TABS];
      if (this.needsInitialSync) {
        const index = next.indexOf('Manual Sync');
        if (index !== -1) next[index] = 'Initial Sync';
      }
      this.subTabs = next;
    },
    async checkInitialSyncStatus() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/config', {
          headers: { 'x-sso-token': ssoToken },
        });
        if (!response.ok) return;
        const config = await response.json();
        this.initialSyncAt = config.initialSyncAt || null;
        this.syncSubTabsAlias();
        if (this.activeSubTab === 'Manual Sync' || this.activeSubTab === 'Initial Sync') {
          this.activeSubTab = this.syncTabKey;
        }
      } catch (error) {
        console.error('Error checking initial sync status:', error);
      }
    },
    async checkConnectionStatus() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/status', {
          headers: { 'x-sso-token': ssoToken },
        });
        if (!response.ok) {
          this.connection = { connected: false, accountValid: false, loaded: true };
          return;
        }
        const data = await response.json();
        this.connection = {
          connected: Boolean(data.connected),
          accountValid: data.accountValid !== false,
          loaded: true,
        };
      } catch (error) {
        this.connection = { ...this.connection, loaded: true };
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        const finish = (value) => {
          window.removeEventListener('message', onMessage);
          resolve(value);
        };
        const timer = setTimeout(() => finish(''), 2500);
        const onMessage = ({ data }) => {
          if (data?.message === 'REQUEST_USER_DATA_RESPONSE') {
            clearTimeout(timer);
            finish(data.payload);
          }
        };
        window.addEventListener('message', onMessage);
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
      });
    },
  },
};
</script>

<style scoped>
.integration {
  width: 100%;
  padding: 8px 0 32px;
  color: #0f172a;
}

.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 4px;
  color: #24acfe;
  font-size: 13px;
  font-weight: 600;
}

.hero h1,
.section-titles h2,
.group-head h2,
.tile-copy strong {
  margin: 0;
  color: #0f172a;
}

.hero h1 {
  font-size: 28px;
  letter-spacing: -0.03em;
}

.hero-sub,
.group-head p,
.section-titles p,
.empty {
  color: #64748b;
}

.hero-sub {
  margin: 6px 0 0;
  font-size: 14px;
  max-width: 560px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e6eef6;
  font-size: 13px;
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
}

.tone-ok {
  color: #047857;
  border-color: #bbf7d0;
  background: #ecfdf5;
}

.tone-ok .status-dot {
  background: #10b981;
}

.tone-warn {
  color: #b45309;
  border-color: #fde68a;
  background: #fffbeb;
}

.tone-warn .status-dot {
  background: #f59e0b;
}

.tone-bad {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fef2f2;
}

.tone-bad .status-dot {
  background: #ef4444;
}

.search-wrap {
  position: relative;
  margin-bottom: 16px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px 12px 40px;
  border: 1px solid #d9e6f2;
  border-radius: 12px;
  background: #fff;
  font-size: 14px;
  color: #0f172a;
}

.search-input:focus {
  outline: none;
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.15);
}

.card {
  background: #fff;
  border: 1px solid #e6eef6;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 18px 20px 20px;
}

.group-card + .group-card {
  margin-top: 16px;
}

.group-head {
  margin-bottom: 14px;
}

.group-head h2 {
  font-size: 17px;
}

.group-head p,
.section-titles p,
.empty {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.45;
}

.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.setting-tile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px;
  border: 1px solid #e6eef6;
  border-radius: 14px;
  background: #f8fbff;
  cursor: pointer;
  color: inherit;
}

.setting-tile:hover {
  border-color: #24acfe;
  background: #fff;
}

.tile-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef7ff;
  color: #24acfe;
  flex-shrink: 0;
}

.tile-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.tile-copy strong {
  font-size: 14px;
}

.tile-copy span {
  color: #64748b;
  font-size: 13px;
  line-height: 1.4;
}

.tile-chevron {
  margin-left: auto;
  color: #94a3b8;
  flex-shrink: 0;
  margin-top: 10px;
}

.section-toolbar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: start;
  margin-bottom: 16px;
}

.back-btn,
.jump-label select {
  border: 1px solid #d9e6f2;
  background: #fff;
  color: #0f172a;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.back-btn:hover {
  border-color: #24acfe;
  color: #24acfe;
}

.section-titles h2 {
  font-size: 22px;
  letter-spacing: -0.03em;
}

.jump-label {
  display: grid;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.jump-label select {
  min-width: 220px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
}

.section-body {
  width: 100%;
}

.empty {
  padding: 24px;
  text-align: center;
}

@media (max-width: 820px) {
  .hero,
  .section-toolbar {
    grid-template-columns: 1fr;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .tile-grid {
    grid-template-columns: 1fr;
  }

  .jump-label,
  .jump-label select {
    width: 100%;
  }
}
</style>
